import { useCallback, useEffect, useRef, useState } from "react";
import type { WdFile } from "@edge/shared";
import {
  ApiError,
  fetchDealWorkdrive,
  fetchDealWorkdriveFileBlob,
  uploadDealWorkdriveFiles,
} from "../../api";
import { WorkDrivePreviewModal } from "./WorkDrivePreviewModal";
import { EmailComposeModal } from "../email/EmailComposeModal";

interface Props {
  dealId: string;
}

interface Crumb {
  id: string;
  name: string;
}

function fmtSize(bytes: number): string {
  if (!bytes) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function fmtDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(d);
}

/**
 * WD-2 — a deal's WorkDrive folder, browsable. The deal folder is a tree (top
 * level = subfolders like Photos / Closing Files), so this navigates one level
 * at a time via the ?folder= route, tracking a breadcrumb. Files open in
 * WorkDrive (permalink, new tab) — no in-Edge download in v1.
 */
export function WorkDriveTab({ dealId }: Props) {
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const [files, setFiles] = useState<WdFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noFolder, setNoFolder] = useState(false);
  const [preview, setPreview] = useState<WdFile | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [composing, setComposing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Navigate to a folder. folderId null = the deal root (no ?folder param).
  // nextCrumbs is the breadcrumb AFTER this navigation.
  const navigate = useCallback(
    async (folderId: string | null, nextCrumbs: Crumb[]) => {
      setLoading(true);
      setError(null);
      try {
        const r = await fetchDealWorkdrive(dealId, folderId ?? undefined);
        if (!r.folder_id) {
          setNoFolder(true);
          setCrumbs([]);
          setFiles([]);
          return;
        }
        setNoFolder(false);
        setSelected(new Set()); // selection is per-folder; reset on navigation
        // Folders first, then files — each alpha — so the tree structure reads
        // at a glance (the ordering Zoho returns is not grouped).
        setFiles(
          [...r.files].sort((a, b) =>
            a.isFolder !== b.isFolder
              ? a.isFolder
                ? -1
                : 1
              : a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
          ),
        );
        // Seed the root crumb the first time (folderId === null).
        setCrumbs(nextCrumbs.length ? nextCrumbs : [{ id: r.folder_id, name: "Deal files" }]);
      } catch (e) {
        setError(e instanceof ApiError ? `${e.code} (HTTP ${e.status})` : "load_failed");
      } finally {
        setLoading(false);
      }
    },
    [dealId],
  );

  useEffect(() => {
    void navigate(null, []);
  }, [navigate]);

  const drillInto = (f: WdFile) => navigate(f.id, [...crumbs, { id: f.id, name: f.name }]);
  const jumpTo = (i: number) => {
    const next = crumbs.slice(0, i + 1);
    void navigate(next[next.length - 1].id, next);
  };
  const retry = () => {
    const last = crumbs[crumbs.length - 1];
    void navigate(last ? last.id : null, crumbs);
  };
  const downloadFile = async (f: WdFile) => {
    setDownloadingId(f.id);
    try {
      const blob = await fetchDealWorkdriveFileBlob(dealId, f.id, true);
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = f.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(u);
    } catch {
      setError("download_failed");
    } finally {
      setDownloadingId(null);
    }
  };
  // Upload into the folder currently being viewed (deepest crumb). Additive.
  const onPickFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-picking the same file
    const current = crumbs[crumbs.length - 1];
    if (!picked.length || !current) return;
    setUploading(true);
    setToast(null);
    try {
      const r = await uploadDealWorkdriveFiles(dealId, current.id, picked);
      setToast(
        `Uploaded ${r.uploaded.length} file${r.uploaded.length === 1 ? "" : "s"} to “${current.name}”.`,
      );
      await navigate(current.id, crumbs); // refresh so the new files appear
    } catch (err) {
      setError(err instanceof ApiError ? `upload_failed (${err.code})` : "upload_failed");
    } finally {
      setUploading(false);
    }
  };

  if (noFolder) {
    return (
      <div className="tab-content">
        <div className="wd-empty">No WorkDrive folder for this deal.</div>
      </div>
    );
  }

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectedFiles = files.filter((f) => !f.isFolder && selected.has(f.id));

  const currentFolderName = crumbs[crumbs.length - 1]?.name ?? "this folder";

  return (
    <div className="tab-content wd-tab">
      <div className="wd-toolbar">
        <div className="wd-breadcrumb">
          {crumbs.map((c, i) => (
            <span key={c.id} className="wd-crumb-wrap">
              {i > 0 && <span className="wd-crumb-sep">/</span>}
              <button
                className="wd-crumb"
                onClick={() => jumpTo(i)}
                disabled={i === crumbs.length - 1}
              >
                {c.name}
              </button>
            </span>
          ))}
        </div>
        <button
          className="wd-upload-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || loading || !crumbs.length}
          title={`Upload file(s) to “${currentFolderName}”`}
        >
          {uploading ? "Uploading…" : "↑ Upload"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => void onPickFiles(e)}
        />
      </div>

      {toast && <div className="wd-toast">{toast}</div>}

      {selected.size > 0 && (
        <div className="wd-selbar">
          <span className="wd-selbar-count">
            {selected.size} file{selected.size === 1 ? "" : "s"} selected
          </span>
          <button className="wd-selbar-btn" onClick={() => setComposing(true)}>
            ✉ Email selected
          </button>
          <button
            className="wd-selbar-clear"
            onClick={() => setSelected(new Set())}
          >
            Clear
          </button>
        </div>
      )}

      {error ? (
        <div className="lme-error">
          ⚠ {error}
          <button className="lme-retry" onClick={retry}>RETRY</button>
        </div>
      ) : loading ? (
        <div className="wd-loading">
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-label">LOADING WORKDRIVE</span>
        </div>
      ) : files.length === 0 ? (
        <div className="wd-empty">This folder is empty.</div>
      ) : (
        <div className="wd-list">
          {files.map((f) =>
            f.isFolder ? (
              <button
                key={f.id}
                className="wd-row wd-row-folder"
                onClick={() => drillInto(f)}
                title="Open folder"
              >
                <span className="wd-check-spacer" />
                <span className="wd-icon">📁</span>
                <span className="wd-name">{f.name}</span>
                <span className="wd-meta">{fmtDate(f.modifiedAt)}</span>
                <span className="wd-chevron">›</span>
              </button>
            ) : (
              <div
                key={f.id}
                className={`wd-row wd-row-file${selected.has(f.id) ? " wd-row-sel" : ""}`}
              >
                <input
                  type="checkbox"
                  className="wd-check"
                  checked={selected.has(f.id)}
                  onChange={() => toggleSelect(f.id)}
                  title="Select to email"
                />
                <button
                  className="wd-file-main"
                  onClick={() => setPreview(f)}
                  title="Preview in Edge"
                >
                  <span className="wd-icon">📄</span>
                  <span className="wd-name">{f.name}</span>
                </button>
                <span className="wd-ext">{f.ext || "—"}</span>
                <span className="wd-size">{fmtSize(f.sizeBytes)}</span>
                <span className="wd-meta">{fmtDate(f.modifiedAt)}</span>
                <button
                  className="wd-action"
                  onClick={() => void downloadFile(f)}
                  disabled={downloadingId === f.id}
                  title="Download to your computer"
                >
                  {downloadingId === f.id ? "…" : "↓"}
                </button>
                <a
                  className="wd-open"
                  href={f.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open in WorkDrive"
                >
                  open ↗
                </a>
              </div>
            ),
          )}
        </div>
      )}

      {preview && (
        <WorkDrivePreviewModal
          dealId={dealId}
          file={preview}
          onClose={() => setPreview(null)}
        />
      )}

      {composing && (
        <EmailComposeModal
          dealId={dealId}
          initialAttachments={selectedFiles.map((f) => ({
            fileId: f.id,
            name: f.name,
            sizeBytes: f.sizeBytes,
          }))}
          onClose={() => setComposing(false)}
          onSent={() => {
            setComposing(false);
            setSelected(new Set());
            setToast("Email sent with the selected file(s) attached.");
          }}
        />
      )}
    </div>
  );
}
